import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { css } from "@styled-system/css";
import { FormikProps } from "formik";
import React, { useCallback, useState } from "react";

import { Alert, Box, Button, Text } from "~/components/blocks";
import { useFetchPharmacy } from "~/components/pages/Pharmacy/use-fetch-pharmacy";

import { Fields } from "../types";
import { useResetErrors } from "../use-reset-errors";
import { FileList } from "./FileList";
import { FilePreview } from "./FilePreview";
import { ACCEPT_TYPES, UploaderButtons } from "./UploaderButtons";

const MAX_FILE_SIZE = 10_485_760; // 10MB

type Props = {
  firstAccess: boolean;
  pharmacyId?: string;
  formik: FormikProps<Fields>;
  onNext: () => void;
};

const Wrap = styled(Box)(({ theme }) =>
  css({
    width: "100%",
    maxWidth: theme.windowSize.base,
    margin: "0 auto",
    padding: "0",
    boxSizing: "border-box",
    [`@media ${theme.mediaQueries.sp}`]: {
      margin: `0 auto`,
    },
  }),
);

export const UploadPrescriptions = React.memo((props: Props) => {
  const { formik } = props;
  const theme = useTheme();

  const [previewingFile, setPreviewingFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { pharmacy } = useFetchPharmacy(props.pharmacyId);

  const isDisabled =
    formik.values.prescriptions?.length === 0 ||
    !!formik.errors.prescriptions ||
    !!error;

  const handleChange = useCallback(
    (_files: FileList) => {
      setError(null);

      const _prescriptions = formik.values.prescriptions
        ? [...formik.values.prescriptions]
        : [];

      for (const file of _files) {
        if (file.size > MAX_FILE_SIZE) {
          setError("画像サイズが大き過ぎます");
          break;
        }
        if (!ACCEPT_TYPES.includes(file.type)) {
          setError("サポートされてないファイル形式です");
          break;
        }
        _prescriptions.push(file);
      }
      formik.setFieldValue("prescriptions", _prescriptions);
    },
    [formik],
  );

  const handleBack = useCallback(() => setPreviewingFile(null), []);
  const handleClick = useCallback((file: File) => setPreviewingFile(file), []);
  const handleDelete = useCallback(
    (file: File) => {
      const newPrescriptions = formik.values.prescriptions?.filter(
        (prescription) => prescription !== file,
      );
      formik.setFieldValue("prescriptions", newPrescriptions);
      handleBack();
    },
    [formik, handleBack],
  );

  useResetErrors(formik, props.firstAccess);

  return previewingFile ? (
    <FilePreview
      file={previewingFile}
      onBack={handleBack}
      onDelete={handleDelete}
    />
  ) : (
    <Wrap>
      <Text block size="m" fontWeight="bold">
        {pharmacy?.webBookingsSetting?.electronicPrescription
          ? "医療機関で渡された処方箋または処方内容（控え）を撮影またはアップロードしてください"
          : "医療機関で渡された処方箋を撮影またはアップロードしてください"}
      </Text>
      <Text block size="m">
        写真を撮る場合は、文字がよく見えるよう全体を撮影してください
      </Text>
      <Box marginTop={theme.space.m}>
        <UploaderButtons onChange={handleChange} />
        {error && (
          <Alert status="error" marginTop={theme.space.m}>
            {error}
          </Alert>
        )}
        {formik.errors.prescriptions && (
          <Alert status="error" marginTop={theme.space.m}>
            {formik.errors.prescriptions}
          </Alert>
        )}
        {formik.values.prescriptions &&
          formik.values.prescriptions.length > 0 && (
            <Box marginTop={theme.space.m}>
              <FileList
                files={formik.values.prescriptions}
                onClick={handleClick}
              />
            </Box>
          )}
        <Button
          use="base"
          wide="fill"
          marginTop={theme.space.xl}
          disabled={isDisabled}
          onClick={props.onNext}
        >
          次は患者情報の入力
        </Button>
      </Box>
    </Wrap>
  );
});

UploadPrescriptions.displayName = "UploadPrescriptions";
